import { TagAction } from '@aibees/elephant/components/see-all-popup';
import { useSearchFiltersStore } from '@aibees/elephant/feature/search-filters/store';
import { useSearchPersonStore } from '@aibees/elephant/feature/search-table-people/store';
import { useSearchTypeFromRoute } from '@aibees/elephant/hooks';
import { ROUTES } from '@aibees/elephant/router';
import { SearchType } from '@aibees/elephant/types';
import {
  Chip,
  CustomPopover,
  Icon,
  IconName,
  usePopover,
} from '@aibees/shared/components';
import { HighlightableContent } from '@aibees/shared/features';
import { classnames } from '@aibees/shared/utils';
import { Typography } from '@mui/material';
import React, { memo, useEffect, useState } from 'react';
import { usePageType } from '../../hooks/use-page-type';
import * as SX from './sx';
import type { TagChipProps as Props } from './types';
import { useAddTagToParam } from './use-add-tag-to-param';

const ANCHOR_ORIGIN = {
  horizontal: 'left' as const,
  vertical: 'bottom' as const,
};
const TRANSFORM_ORIGIN = {
  vertical: 'top' as const,
  horizontal: 'left' as const,
};

export const TagChip = memo(({ className, item, config }: Props) => {
  const setCurrentItemId = useSearchFiltersStore.useSetSelectedItemId();
  const setId = useSearchPersonStore((state) => state.setId);
  const type = useSearchTypeFromRoute();
  const { isPeoplePage } = usePageType();
  const addTagToParam = useAddTagToParam();

  const exclude = config?.excludeSearch || `exclude_${config?.field}`;
  const include = config?.includeSearch || `include_${config?.field}`;
  const currentURL =
    type === SearchType.People ? ROUTES.searchPeople : ROUTES.searchCompany;
  const [hoveredTag, setHoveredTag] = useState<string>();
  const popoverContentState = usePopover();

  const handleOpenPopup = (
    event: React.MouseEvent<HTMLElement>,
    value: string
  ) => {
    if (config?.actions && config.field) {
      popoverContentState.onOpen(event.currentTarget);
      setHoveredTag(value);
    }
    return null;
  };

  useEffect(() => {
    if (!popoverContentState.open) setHoveredTag('');
  }, [popoverContentState]);

  const handleClickAction = (action: TagAction) => {
    if (action === TagAction.INCLUDE_IN_EXISTING_SEARCH) {
      if (hoveredTag) {
        addTagToParam(include, hoveredTag);
      }
      if (isPeoplePage || type === SearchType.People) {
        setId(undefined);
      } else {
        setCurrentItemId(undefined);
      }
    }
    if (action === TagAction.EXCLUDE_IN_EXISTING_SEARCH) {
      if (hoveredTag) {
        addTagToParam(exclude, hoveredTag);
      }
      if (isPeoplePage || type === SearchType.People) {
        setId(undefined);
      } else {
        setCurrentItemId(undefined);
      }
    }

    if (action === TagAction.EXCLUDE_IN_NEW_SEARCH) {
      const modifiedURL = `${currentURL}?${exclude}=${hoveredTag}`;
      window.open(modifiedURL, '_blank');
    }

    if (action === TagAction.INCLUDE_IN_NEW_SEARCH) {
      const modifiedURL = `${currentURL}?${include}=${hoveredTag}`;

      window.open(modifiedURL, '_blank');
    }
    popoverContentState.onClose();
  };

  return (
    <>
      {/* <Styled.Wrapper
        className={classnames('TagChip', className)}
        label={<HighlightableContent content={item.label} fontWeight={500} />}
        variant="filled"
        size="xsmall"
        onClick={(e) => handleOpenPopup(e, item.label)}
        color="primary"
        startIcon={item.startIcon}
        {...item.props}
      /> */}

      <Chip
        className={classnames('TagChip', className)}
        icon={<Icon name={item.startIcon as IconName} />}
        color="primary"
        onClick={(e) => handleOpenPopup(e, item.label)}
        size="small"
        variant="filled"
        label={
          item.customLabel ?? (
            <HighlightableContent content={item.label} fontWeight={500} />
          )
        }
      />

      {config?.actions && (
        <CustomPopover
          state={popoverContentState}
          anchorOrigin={ANCHOR_ORIGIN}
          transformOrigin={TRANSFORM_ORIGIN}
        >
          {config?.actions.map((x) => (
            <Typography
              key={x}
              variant="body4"
              sx={SX.subPopoverItem}
              onClick={() => handleClickAction(x)}
            >
              {x}
            </Typography>
          ))}
        </CustomPopover>
      )}
    </>
  );
});

TagChip.displayName = 'TagChip';
